// src/services/saasNotificationApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

// Types
export interface SaasNotification {
  _id: string;
  title: string;
  body: string;
  businessId: string;
  userId?: string;
  type: 'system' | 'weather' | 'project' | 'task' | 'signature' | 'invoice' | 'alert' | 'ticket';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  channels: ('app' | 'email' | 'sms' | 'push')[];
  readAt?: Date;
  expiresAt?: Date;
  actionData?: {
    type?: string;
    url?: string;
    entityId?: string;
    entityType?: string;
  };
  reference?: {
    type: string;
    id: string;
  };
  isBroadcast: boolean;
  metadata: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export interface NotificationStats {
  total: number;
  unread: number;
  highPriority: number;
  byType: Record<string, number>;
  byStatus: Record<string, number>;
  recentCount: number;
}

export interface NotificationResponse {
  notifications: SaasNotification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  stats: NotificationStats;
  success: boolean;
}

export interface NotificationFilters {
  businessId: string;
  type?: string;
  status?: string;
  priority?: string;
  page?: number;
  limit?: number;
  fromDate?: string;
  toDate?: string;
}

export const saasNotificationApi = createApi({
  reducerPath: 'saasNotificationApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}notifications`,
    prepareHeaders: (headers) => {
      // Add standard headers
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });

      // Add business-specific API key
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }

      return headers;
    },
  }),
  tagTypes: ['Notifications', 'NotificationStats'],
  endpoints: (builder) => ({
    // Get business notifications
    getBusinessNotifications: builder.query<NotificationResponse, NotificationFilters>({
      query: ({ businessId, ...filters }) => ({
        url: `/business/${businessId}`,
        params: filters
      }),
      // @ts-ignore
      providesTags: (result, error, { businessId }) => [
        { type: 'Notifications', id: businessId },
        'Notifications'
      ]
    }),

    // Get user notifications
    getUserNotifications: builder.query<{ notifications: SaasNotification[]; success: boolean }, {
      userId: string;
      businessId: string;
      type?: string;
      status?: string;
      page?: number;
      limit?: number;
    }>({
      query: ({ userId, businessId, ...filters }) => ({
        url: `/user/${userId}`,
        params: { businessId, ...filters }
      }),
       // @ts-ignore
      providesTags: (result, error, { userId }) => [
        { type: 'Notifications', id: userId },
        'Notifications'
      ]
    }),

    // Get notification statistics
    getNotificationStats: builder.query<{ stats: NotificationStats; success: boolean }, string>({
      query: (businessId) => ({
        url: `/business/${businessId}/stats`
      }),
       // @ts-ignore
      providesTags: (result, error, businessId) => [
        { type: 'NotificationStats', id: businessId }
      ]
    }),

    // Mark notification as read
    markNotificationAsRead: builder.mutation<
      { notification: SaasNotification; success: boolean; message: string },
      { notificationId: string; businessId: string }
    >({
      query: ({ notificationId, businessId }) => ({
        url: `/${notificationId}/read`,
        method: 'PUT',
        params: { businessId }
      }),
      // @ts-ignore
      invalidatesTags: (result, error, { businessId }) => [
        { type: 'Notifications', id: businessId },
        { type: 'NotificationStats', id: businessId },
        'Notifications'
      ]
    }),

    // Mark all notifications as read
    markAllNotificationsAsRead: builder.mutation<
      { updated: number; success: boolean; message: string },
      { businessId: string; notificationIds?: string[] }
    >({
      query: ({ businessId, notificationIds }) => ({
        url: `/business/${businessId}/read-all`,
        method: 'PUT',
        body: notificationIds ? { notificationIds } : {}
      }),
      // @ts-ignore
      invalidatesTags: (result, error, { businessId }) => [
        { type: 'Notifications', id: businessId },
        { type: 'NotificationStats', id: businessId },
        'Notifications'
      ]
    }),

    // Delete notification
    deleteNotification: builder.mutation<
      { success: boolean; message: string },
      { notificationId: string; businessId: string }
    >({
      query: ({ notificationId, businessId }) => ({
        url: `/${notificationId}`,
        method: 'DELETE',
        params: { businessId }
      }),
      // @ts-ignore
      invalidatesTags: (result, error, { businessId }) => [
        { type: 'Notifications', id: businessId },
        { type: 'NotificationStats', id: businessId },
        'Notifications'
      ]
    }),

    // Get filtered notifications (more advanced)
    getFilteredNotifications: builder.query<NotificationResponse, NotificationFilters & {
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }>({
      query: ({ businessId, ...filters }) => ({
        url: `/business/${businessId}`,
        params: filters
      }),
      // @ts-ignore
      providesTags: (result, error, { businessId }) => [
        { type: 'Notifications', id: `${businessId}-filtered` },
        'Notifications'
      ]
    })
  })
});

export const {
  useGetBusinessNotificationsQuery,
  useLazyGetBusinessNotificationsQuery,
  useGetUserNotificationsQuery,
  useGetNotificationStatsQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useDeleteNotificationMutation,
  useGetFilteredNotificationsQuery
} = saasNotificationApi;